import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FaChevronDown, FaChevronUp } from 'react-icons/fa';

// Assets
import Ruben from '../AllImages/Rubenn.png';
import Jesus from '../AllImages/Jesus.jpeg';
import BrushOne from '../Assets/BrushOne.png';
import BrushTwo from '../Assets/BrushTwo.png';
import BrushThree from '../Assets/BrushThree.png';
import BrushFour from '../Assets/BrushFour.png';

function BrushBackgrounds() {
  const brushes = [
    { src: BrushOne, style: { top: '5%', left: '5%', width: '120px', rotate: '-10deg' } },
    { src: BrushTwo, style: { top: '40%', right: '5%', width: '140px', rotate: '12deg' } },
    { src: BrushThree, style: { bottom: '20%', left: '10%', width: '100px', rotate: '-8deg' } },
    { src: BrushFour, style: { bottom: '10%', right: '20%', width: '130px', rotate: '15deg' } },
  ];
  return (
    <div className="absolute inset-0 z-0 pointer-events-none opacity-20">
      {brushes.map((b, i) => (
        <img
          key={i}
          src={b.src}
          alt="brush"
          className="absolute"
          style={{ ...b.style, transform: `rotate(${b.style.rotate})` }}
        />
      ))}
    </div>
  );
}

const faqs = [
  {
    question: "Where will rehearsals take place, and how can I access them?",
    answer:
      "The rehearsal location will be detailed in your itinerary. If the information is not yet available, please note that we are in the final stages of selecting the venue and will update you as soon as the details are confirmed.",
  },
  {
    question: "Are there any additional fees for instrument rental or shipping?",
    answer:
      "There are no additional fees. Simanof will manage the rental of instruments such as cellos, bass, and percussion at no extra cost to you.",
  },
  {
    question: "Is there a dress code for the performance?",
    answer:
      "Yes, we kindly request that all participants adhere to a 'Concert Black' dress code to ensure an elegant and unified appearance during the performance.",
  },
  {
    question: "Can friends and family purchase tickets to the performance?",
    answer:
      "Our concerts are open to the public and free of charge. We warmly welcome friends and family to join us for this special event.",
  },
  {
    question: "Is accommodation included or recommended?",
    answer:
      "Accommodation, along with transportation, dining, and attractions, is included in the tour package. We have arranged these details to ensure your complete comfort and convenience throughout the tour.",
  },
  {
    question: "What is the refund policy if I need to cancel?",
    answer:
      "We regret to inform you that, due to our commitment to advance bookings for attractions, hotels, and restaurants, we are unable to offer refunds once payment is received. We appreciate your understanding in this matter.",
  },
  {
    question: "Will there be any optional tours or sightseeing opportunities?",
    answer:
      "Optional tours and sightseeing opportunities are available. If you are over the age of 18 or accompanied by an adult, please speak with our staff for further details, and we will be delighted to assist you.",
  },
  {
    question: "How do I handle special requests or accessibility needs?",
    answer:
      "For any special requests or accessibility needs, please do not hesitate to reach out to our staff at Information@simanof.org or call 773-710-9953. We are dedicated to ensuring a comfortable and inclusive experience for all.",
  },
];

export default function Support() {
  const [openIndex, setOpenIndex] = useState(null);
  const toggleFAQ = (i) => setOpenIndex(openIndex === i ? null : i);

  return (
    <div className="relative min-h-screen bg-gradient-to-br from-gray-100 to-gray-50 py-12">
      <BrushBackgrounds />

      <div className="max-w-6xl mx-auto bg-white rounded-3xl shadow-2xl overflow-hidden">
        {/* Header */}
        <header className="bg-red-600 text-white text-center py-8 px-4">
          <h1 className="text-4xl font-serif font-bold">Support &amp; FAQs</h1>
          <p className="mt-2 text-lg">
            Need help? Find answers below or contact Ruben or Jesus directly.
          </p>
        </header>

        <div className="md:flex">
          {/* Contacts */}
          <div className="md:w-1/3 bg-gray-50 p-8 space-y-8">
            {[
              {
                name: 'Ruben Siman',
                role: 'Executive Director',
                img: Ruben,
                phone: '+1 (773) 710-9953',
                email: 'Information@simanof.org',
              },
              {
                name: 'Jesus Azucar',
                role: 'Assisting Coordinator',
                img: Jesus,
                phone: '+1 (689) 242-2621',
                email: 'Production@simanof.org',
              },
            ].map((c, i) => (
              <motion.div
                key={i}
                whileHover={{ scale: 1.02 }}
                transition={{ duration: 0.2 }}
                className="bg-white rounded-xl shadow-lg p-6 text-center"
              >
                {/* ↑ Increased avatar size here ↓ */}
                <img
                  src={c.img}
                  alt={c.name}
                  className="mx-auto w-40 h-36 object-cover rounded-full mb-4 "
                />
                <h2 className="text-xl font-semibold text-red-600">{c.name}</h2>
                <p className="text-gray-700">{c.role}</p>
                <p className="mt-2 text-gray-600">{c.phone}</p>
                <p className="text-gray-600">{c.email}</p>
              </motion.div>
            ))}
          </div>

          {/* FAQ */}
          <div className="md:w-2/3 p-8">
            <h2 className="text-2xl font-semibold text-red-600 mb-6">
              Frequently Asked Questions
            </h2>
            {faqs.map((faq, i) => (
              <div key={i} className="mb-4">
                <button
                  onClick={() => toggleFAQ(i)}
                  className="w-full flex justify-between items-center bg-red-50 hover:bg-red-100 rounded-lg px-4 py-3 focus:outline-none"
                >
                  <span className="text-gray-800 font-medium">{faq.question}</span>
                  <span className="text-red-600">
                    {openIndex === i ? <FaChevronUp /> : <FaChevronDown />}
                  </span>
                </button>

                <AnimatePresence>
                  {openIndex === i && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.3 }}
                      className="overflow-hidden bg-white px-6 py-4 text-gray-700 rounded-b-lg border-l-4 border-red-600"
                    >
                      {faq.answer}
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
